import React, { ReactElement, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../contexts/ServiceContext';
import BackButton from '../BackButton';
import Icon from '../Icon';
import { PlusIcon, UserIcon, BriefcaseIcon, ChatBubbleBottomCenterIcon, CheckIcon } from '@heroicons/react/24/solid';
import BasicInfo from './tab/BasicInfo';
import Professional from './tab/Professional';
import Introduction from './tab/Introduction';
import Button, { ButtonVariant } from '../kit/Button';
import { Contact } from 'common/model';

interface ContactFormProps {
    onSubmit: (formData: any) => void;
    contact?: Contact;
    error?: string;
}

export default function ContactForm({ onSubmit, contact, error }: ContactFormProps) {
    const { t } = useTranslation();
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Track button state: 'idle', 'loading', or 'success'
    const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');

    const [formData, setFormData] = useState({
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        jobTitle: undefined,
        companyName: undefined,
        companyWebsite: undefined,
        linkedInProfile: undefined,
        introductionParagraph: undefined,
        ...contact
    });

    // When the contact prop is updated (after successful save), show thumbs up
    useEffect(() => {
        if (contact) {
            setFormData(prev => ({ ...prev, ...contact }));
        }
    }, [contact]);

    // If an external error occurs, revert back to idle so they can fix it
    useEffect(() => {
        if (error) {
            setStatus('idle');
        }
    }, [error]);

    const handleUpdate = (updatedFields: any) => {
        // Reset button to default state when user types/changes something
        if (status !== 'idle') {
            setStatus('idle');
        }
        setFormData(prev => ({ ...prev, ...updatedFields }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        try {
            await onSubmit(formData);
            setStatus('success');
        } catch (err) {
            setStatus('idle');
        }
    };

    const TABS = [
        {
            id: "basic_info",
            component: <BasicInfo contact={formData} onUpdate={handleUpdate} />,
            pillComponent: (
                <div className='inline-flex items-center space-x-2 text-lg'>
                    <Icon iconClass={'h-5 w-5 mr-2'} icon={<UserIcon />} />
                    {t("add_new_contact_basic_info_tab_title")}
                </div>
            ),
        },
        {
            id: "professional_info",
            component: <Professional contact={formData} onUpdate={handleUpdate} />,
            pillComponent: (
                <div className='inline-flex items-center space-x-2 text-lg'>
                    <Icon iconClass={'h-5 w-5 mr-2'} icon={<BriefcaseIcon />} />
                    {t("add_new_contact_professional_tab_title")}
                </div>
            ),
        },
        {
            id: "introduction",
            component: <Introduction contact={formData} onUpdate={handleUpdate} />,
            pillComponent: (
                <div className='inline-flex items-center space-x-2 text-lg'>
                    <Icon iconClass={'h-5 w-5 mr-2'} icon={<ChatBubbleBottomCenterIcon />} />
                    {t("add_new_contact_introduction_tab_title")}
                </div>
            ),
        }
    ];

    const TAB_WIDTH_PERCENT = 100 / TABS.length;

    return (
        <div className="h-screen flex flex-col justify-center pt-4">
            <div className='w-full max-w-3xl mx-auto'>
                <div className="mt-32 mb-4 self-center">
                    <BackButton path="/app/home" page="Dashboard" />
                </div>

                <form onSubmit={handleSubmit} className="bg-white px-6 py-12 rounded-lg shadow-xl space-y-5">
                    <div className="flex justify-center items-center">
                        <Icon icon={<PlusIcon />} iconClass={'text-green-600 h-16 w-16 bg-green-100 p-3 rounded-full strong'} />
                    </div>

                    <h1 className="text-2xl font-bold mb-2 text-center">
                        {contact ? t('edit_contact_title') : t('add_new_contact_title')}
                    </h1>

                    <p className="text-gray-600 text-lg text-center">{t('add_new_contact_description')}</p>

                    {/* Tab Navigation */}
                    <div className="bg-gray-100 p-2 rounded-lg">
                        <div className="flex w-full relative">
                            <div
                                className="absolute -top-0 h-full bg-white rounded-lg transition-all duration-300 shadow-sm"
                                style={{
                                    width: `${TAB_WIDTH_PERCENT}%`,
                                    transform: `translateX(${activeTabIndex * 100}%)`
                                }}
                            />

                            {TABS.map((tab, index) => (
                                <button
                                    id={tab.id}
                                    key={index}
                                    type="button"

                                    onClick={() => setActiveTabIndex(index)}
                                    className={`flex-1 text-center px-4 py-2 text-sm font-medium transition-colors relative z-10 ${activeTabIndex === index ? 'text-green-600' : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {tab.pillComponent}
                                </button>
                            ))}
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                            <div className="flex">
                                <div className="ml-3">
                                    <p className="text-sm text-red-700 font-medium">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-4 p-4 bg-white rounded-lg min-h-[300px]">
                        {TABS[activeTabIndex].component}
                    </div>

                    {/* Dynamic Submit Button */}
                    <button
                        type="submit"
                        disabled={status === 'loading' || status === 'success'}
                        id="contact-form-submit"
                        className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-bold text-white transition-all duration-200 ${status === 'success'
                            ? 'bg-blue-600 hover:bg-blue-700'
                            : 'bg-green-500 hover:bg-green-600'
                            } disabled:opacity-70 disabled:cursor-not-allowed`}
                    >
                        {status === 'loading' ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('saving_label', 'Saving...')}
                            </>
                        ) : status === 'success' ? (
                            <>
                                <CheckIcon className="h-5 w-5 mr-2" />
                                {t('saved_label', 'Saved!')}
                            </>
                        ) : (
                            contact ? t('edit_contact_submit_button') : t('add_new_contact_submit_button')
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
